import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  Fade, 
  Chip,
  Paper,
  Avatar
} from '@mui/material';
import { 
  Favorite, 
  Psychology, 
  Groups, 
  Star, 
  CheckCircle,
  Transform,
  EmojiEvents,
  AutoAwesome
} from '@mui/icons-material';

const RelationshipsLandingPage = () => {
  const handleGetStarted = () => {
    window.location.href = '/signup';
  };

  const handleLogin = () => {
    window.location.href = '/login';
  };

  const features = [
    {
      icon: <Transform sx={{ fontSize: '3rem', color: '#E91E63' }} />,
      title: "Deep Relationship Healing",
      description: "Transform broken connections, heal emotional wounds, and rebuild trust from the ground up using proven therapeutic methods."
    },
    {
      icon: <Psychology sx={{ fontSize: '3rem', color: '#FF5722' }} />,
      title: "Revolutionary Communication",
      description: "Master heart-to-heart connection, resolve conflicts with love, and create conversations that strengthen your bond."
    },
    {
      icon: <AutoAwesome sx={{ fontSize: '3rem', color: '#9C27B0' }} />,
      title: "Soulmate Magnetism",
      description: "Attract your ideal partner, increase emotional intimacy, and create the deep soul connection you've always dreamed of."
    }
  ];

  const testimonials = [
    {
      name: "Sarah & Michael",
      role: "Marriage Transformation",
      result: "Saved marriage after 15 years",
      text: "We were on the brink of divorce. This program taught us how to really see and hear each other again. Our love is deeper now than when we first met.",
      avatar: "💕"
    },
    {
      name: "Emma",
      role: "Single to Soulmate",
      result: "Found true love in 6 months",
      text: "After years of dating disasters, this program helped me heal my attachment patterns and attract the most amazing, loving partner I could have imagined.",
      avatar: "✨"
    },
    {
      name: "James & Lisa",
      role: "Breakthrough Communication",
      result: "Ended 20 years of silent treatment",
      text: "We hadn't truly talked in years - just logistics and surface stuff. Now we have the deepest conversations and intimacy we've ever experienced. Our love is completely renewed.",
      avatar: "🌟"
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)',
      position: 'relative'
    }}>
      {/* Powerful Animated Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.15,
        background: `
          radial-gradient(circle at 25% 25%, #E91E63 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, #FF5722 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, #9C27B0 0%, transparent 70%),
          linear-gradient(45deg, transparent 40%, rgba(233, 30, 99, 0.1) 50%, transparent 60%)
        `,
        animation: 'backgroundPulse 8s ease-in-out infinite alternate'
      }} />

      {/* CSS Animation */}
      <style>
        {`
          @keyframes backgroundPulse {
            0% { opacity: 0.1; }
            100% { opacity: 0.2; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(233, 30, 99, 0.3); }
            50% { box-shadow: 0 0 40px rgba(233, 30, 99, 0.6), 0 0 60px rgba(233, 30, 99, 0.4); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}
      </style>

      {/* Premium Header */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 10,
        py: { xs: 1, md: 1.5 },
        px: { xs: 2, md: 4 },
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #E91E63, #FF5722)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(233, 30, 99, 0.4)',
              animation: 'glow 3s ease-in-out infinite'
            }}>
              <img src="/sanity_logo.png" alt="Sanity Logo" style={{ 
                width: 36, 
                height: 36,
                filter: 'brightness(1.2)'
              }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                fontSize: { xs: '1.4rem', md: '1.8rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.5)'
              }}>
                Sanity Reps
              </Typography>
              <Typography variant="caption" sx={{ 
                color: '#E91E63',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}>
                RELATIONSHIP HEALING
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleLogin}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.3)',
                px: { xs: 2, md: 3 },
                py: 1,
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                fontWeight: 'bold',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                '&:hover': { 
                  borderColor: '#E91E63',
                  bgcolor: 'rgba(233, 30, 99, 0.1)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Login
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGetStarted}
              sx={{ 
                background: 'linear-gradient(45deg, #E91E63, #FF5722)',
                color: 'white',
                fontWeight: 'bold',
                px: { xs: 2.5, md: 4 },
                py: 1.2,
                fontSize: { xs: '0.85rem', md: '1rem' },
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(233, 30, 99, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(45deg, #C2185B, #E64A19)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 35px rgba(233, 30, 99, 0.6)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              💕 HEAL NOW
            </Button>
          </Box>
        </Box>
      </Box>

      {/* HERO: Complete Relationship Healing */}
      <Container maxWidth="xl" sx={{ 
        position: 'relative', 
        zIndex: 10, 
        pt: { xs: 2, md: 2 },
        pb: { xs: 3, md: 2 },
        minHeight: { md: 'calc(100vh - 80px)' },
        maxHeight: { md: 'calc(100vh - 80px)' },
        display: { md: 'flex' },
        alignItems: { md: 'center' }
      }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ height: '100%' }}>
          {/* LEFT: Compelling Copy */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Box sx={{ pr: { md: 3 } }}>
                {/* Attention-Grabbing Badge */}
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label="💕 DEEP RELATIONSHIP HEALING SYSTEM" 
                    sx={{ 
                      background: 'linear-gradient(45deg, rgba(233, 30, 99, 0.2), rgba(255, 87, 34, 0.2))',
                      color: '#E91E63',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      border: '1px solid rgba(233, 30, 99, 0.5)',
                      backdropFilter: 'blur(10px)',
                      px: 2,
                      py: 0.5,
                      animation: 'float 3s ease-in-out infinite'
                    }} 
                  />
                </Box>

                {/* Power Headline */}
                <Typography variant="h1" sx={{ 
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem', lg: '3.8rem' },
                  fontWeight: 900,
                  color: 'white',
                  mb: { xs: 2, md: 1.5 },
                  lineHeight: 0.95,
                  textShadow: '0 4px 20px rgba(0,0,0,0.7)',
                  letterSpacing: '-0.02em'
                }}>
                  HEAL YOUR
                  <br />
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(45deg, #E91E63, #FF5722, #9C27B0)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    RELATIONSHIPS
                  </Box>
                  <br />
                  <Box component="span" sx={{ color: '#9C27B0' }}>
                    COMPLETELY
                  </Box>
                </Typography>
                
                {/* Power Subheadline */}
                <Typography variant="h4" sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' },
                  color: 'rgba(255,255,255,0.95)',
                  mb: { xs: 2.5, md: 3 },
                  fontWeight: 400,
                  lineHeight: 1.3,
                  maxWidth: '600px'
                }}>
                  Transform broken connections into thriving partnerships. Heal emotional wounds, attract soul-deep love, and create the <strong style={{color: '#E91E63'}}>unbreakable bonds</strong> you've always dreamed of.
                </Typography>

                {/* Rapid Benefits */}
                <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                  <Grid container spacing={2}>
                    {[
                      { icon: '💕', text: 'Deep relationship healing in 90 days', color: '#E91E63' },
                      { icon: '✨', text: 'Transform conflict into intimacy', color: '#FF5722' },
                      { icon: '🌟', text: 'Attract your ideal soulmate', color: '#9C27B0' }
                    ].map((benefit, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          p: { xs: 1.5, md: 2 },
                          borderRadius: 3,
                          background: 'rgba(255,255,255,0.05)',
                          border: `1px solid rgba(${benefit.color === '#E91E63' ? '233, 30, 99' : benefit.color === '#FF5722' ? '255, 87, 34' : '156, 39, 176'}, 0.3)`,
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            background: 'rgba(255,255,255,0.1)'
                          }
                        }}>
                          <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{benefit.icon}</Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.9rem' }
                          }}>
                            {benefit.text}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 3 }, 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 3, md: 0 }
                }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleGetStarted}
                    sx={{ 
                      background: 'linear-gradient(45deg, #E91E63, #FF5722)',
                      color: 'white',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '1rem', md: '1.3rem' },
                      fontWeight: 'bold',
                      borderRadius: 4,
                      boxShadow: '0 12px 35px rgba(233, 30, 99, 0.4)',
                      minWidth: { sm: '280px' },
                      '&:hover': { 
                        background: 'linear-gradient(45deg, #C2185B, #E64A19)',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 45px rgba(233, 30, 99, 0.6)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    💕 START HEALING
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => document.getElementById('testimonials').scrollIntoView({ behavior: 'smooth' })}
                    sx={{ 
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      px: 3,
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      borderRadius: 4,
                      backdropFilter: 'blur(10px)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    💫 See Success Stories
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          
          {/* RIGHT: Powerful Relationship Visual */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1200}>
              <Box sx={{ 
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: 'auto', md: '420px' },
                mt: { xs: 2, md: 0 }
              }}>
                {/* Main Relationship Image */}
                <Box sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                  border: '3px solid rgba(233, 30, 99, 0.5)',
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.4s ease',
                  maxWidth: { xs: '100%', md: '400px' },
                  width: '100%',
                  animation: 'float 6s ease-in-out infinite',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-10px)',
                    boxShadow: '0 35px 80px rgba(0,0,0,0.5)',
                    border: '3px solid rgba(233, 30, 99, 0.8)'
                  }
                }}>
                  <img 
                    src="/relationship.png" 
                    alt="Relationship Healing - Transform Your Love Life" 
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                  
                  {/* Premium Before/After Labels */}
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: 'linear-gradient(135deg, #dc3545, #c82333)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(220, 53, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'pulse 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(220, 53, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    💔 Before: Broken
                  </Box>
                  
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(40, 167, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'glow 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(40, 167, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    💕 After: Connected
                  </Box>
                  
                  {/* Results Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
                    color: 'white',
                    p: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#E91E63' }}>
                      💕 RELATIONSHIP TRANSFORMATION
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { number: "∞", label: "Love", color: '#E91E63' },
                        { number: "100%", label: "Trust", color: '#FF5722' },
                        { number: "Deep", label: "Connection", color: '#9C27B0' }
                      ].map((stat, index) => (
                        <Grid item xs={4} key={index}>
                          <Typography variant="h5" sx={{ 
                            color: stat.color, 
                            fontWeight: 'bold',
                            textShadow: `0 2px 10px ${stat.color}50`
                          }}>
                            {stat.number}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}>
                            {stat.label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* Floating Achievement Indicators */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  left: -20,
                  background: 'linear-gradient(45deg, #E91E63, #FF5722)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(233, 30, 99, 0.4)',
                  animation: 'float 4s ease-in-out infinite'
                }}>
                  ⭐ 5/5 Healing
                </Box>
                
                <Box sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  background: 'linear-gradient(45deg, #FF5722, #9C27B0)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(255, 87, 34, 0.4)',
                  animation: 'float 4s ease-in-out infinite 2s'
                }}>
                  💕 8,500+ Healed
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* FEATURES: Healing Methods - Purple/Indigo Theme */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        position: 'relative',
        background: 'linear-gradient(135deg, #1a0b3d 0%, #2d1b69 30%, #4a1c96 60%, #6a1b9a 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 30% 30%, rgba(156, 39, 176, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(74, 20, 140, 0.4) 0%, transparent 50%)
          `,
          opacity: 0.8
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip 
                label="🔥 REVOLUTIONARY HEALING METHODS" 
                sx={{ 
                  mb: 3, 
                  background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.3), rgba(74, 20, 140, 0.3))',
                  color: '#AB47BC',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  border: '1px solid rgba(156, 39, 176, 0.5)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 1
                }} 
              />
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: 'white',
                mb: 3,
                textShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                Transform Your Love Life
                <Box component="span" sx={{ 
                  background: 'linear-gradient(45deg, #AB47BC, #8E24AA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'block'
                }}>
                  With Deep Healing
                </Box>
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 400,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4
              }}>
                Science-backed methods to heal relationship trauma, attract genuine love, and create unbreakable emotional bonds
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1200 + index * 200}>
                  <Card sx={{ 
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                      boxShadow: '0 20px 50px rgba(156, 39, 176, 0.3)'
                    }
                  }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.2), rgba(74, 20, 140, 0.2))',
                        border: '2px solid rgba(156, 39, 176, 0.4)',
                        mb: 2
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        mb: 2,
                        textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.6,
                        fontSize: '1.1rem'
                      }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS: Success Stories - Slate/Charcoal Theme */}
      <Box id="testimonials" sx={{ 
        py: { xs: 8, md: 12 },
        position: 'relative',
        background: 'linear-gradient(135deg, #263238 0%, #37474f 30%, #455a64 60%, #546e7a 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 25% 25%, rgba(69, 90, 100, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(84, 110, 122, 0.3) 0%, transparent 50%)
          `,
          opacity: 0.8
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip 
                label="✨ REAL RELATIONSHIP TRANSFORMATIONS" 
                sx={{ 
                  mb: 3, 
                  background: 'linear-gradient(45deg, rgba(69, 90, 100, 0.3), rgba(84, 110, 122, 0.3))',
                  color: '#78909C',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  border: '1px solid rgba(69, 90, 100, 0.5)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 1
                }} 
              />
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: 'white',
                mb: 3,
                textShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                Love Stories That Will
                <Box component="span" sx={{ 
                  background: 'linear-gradient(45deg, #78909C, #607D8B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'block'
                }}>
                  Inspire Your Heart
                </Box>
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 400,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4
              }}>
                See how our healing methods transformed broken relationships into soul-deep connections
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1200 + index * 200}>
                  <Card sx={{ 
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    p: 4,
                    height: '100%',
                    position: 'relative',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                      boxShadow: '0 20px 50px rgba(69, 90, 100, 0.3)'
                    }
                  }}>
                    {/* Success Badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: -10,
                      right: 20,
                      background: 'linear-gradient(45deg, #4CAF50, #66BB6A)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 3,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)'
                    }}>
                      ✅ {testimonial.result}
                    </Box>

                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: 'linear-gradient(45deg, #78909C, #607D8B)', 
                          mr: 2,
                          width: 56,
                          height: 56,
                          fontSize: '1.5rem'
                        }}>
                          {testimonial.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: 'white',
                            textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                          }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ 
                            color: '#78909C',
                            fontWeight: 600
                          }}>
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        fontStyle: 'italic',
                        mb: 3
                      }}>
                        "{testimonial.text}"
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {[1,2,3,4,5].map((star) => (
                          <Star key={star} sx={{ color: '#FFD700', fontSize: '1.5rem' }} />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FINAL CTA: Begin Your Healing Journey */}
      <Container maxWidth="md" sx={{ 
        position: 'relative', 
        zIndex: 10, 
        py: { xs: 8, md: 12 },
        textAlign: 'center'
      }}>
        <Paper sx={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          backdropFilter: 'blur(30px)',
          border: '2px solid rgba(233, 30, 99, 0.3)',
          borderRadius: 6,
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
          animation: 'glow 4s ease-in-out infinite'
        }}>
          <Typography variant="h3" sx={{ 
            fontSize: { xs: '2rem', md: '2.8rem' },
            fontWeight: 'bold',
            color: 'white',
            mb: 2,
            textShadow: '0 4px 20px rgba(0,0,0,0.5)'
          }}>
            Ready to Heal Your Heart?
          </Typography>
          
          <Typography variant="h5" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            color: 'rgba(255,255,255,0.9)',
            mb: 4,
            maxWidth: '500px',
            margin: '0 auto 2rem auto',
            lineHeight: 1.4
          }}>
            Join thousands who've transformed their relationships and found the deep, lasting love they deserve
          </Typography>

          <Button 
            variant="contained" 
            size="large"
            onClick={handleGetStarted}
            sx={{ 
              background: 'linear-gradient(45deg, #E91E63, #FF5722)',
              color: 'white',
              px: { xs: 4, md: 6 },
              py: { xs: 2, md: 2.5 },
              fontSize: { xs: '1.1rem', md: '1.4rem' },
              fontWeight: 'bold',
              borderRadius: 5,
              boxShadow: '0 15px 40px rgba(233, 30, 99, 0.4)',
              '&:hover': { 
                background: 'linear-gradient(45deg, #C2185B, #E64A19)',
                transform: 'translateY(-5px)',
                boxShadow: '0 20px 50px rgba(233, 30, 99, 0.6)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            💕 Begin My Healing Journey
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default RelationshipsLandingPage;
